/**
 * SensitiveContent
 * ─────────────────
 * Full-protection wrapper:
 * On screenshot or screen recording violation:
 *  1. Checks if current module is enabled in screenshotProtectedModules.
 *  2. Calls backend /security/violation/block.
 *  3. If backend returns blocked=true  → full suspension screen (permanent).
 *  4. If backend returns warned=true   → in-screen warning banner (grace attempt),
 *     candidate can continue until the threshold is reached.
 *  5. Respects strictScreenshotLock: if disabled, shows a soft warning only.
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useScreenProtection } from './useScreenProtection';
import { useVideoOverlayProtection } from './useVideoOverlayProtection';
import { useSecurityContext } from './SecurityContext';
import DynamicWatermark from './DynamicWatermark';
import { buildUrl } from '../services/api';
import './security.css';

export type ProtectedModuleType = 'exam' | 'results' | 'documents' | 'classes' | 'dashboard';

interface SensitiveContentProps {
  children: React.ReactNode;
  userId?: string;
  module?: ProtectedModuleType;
  showWatermark?: boolean;
  watermarkColor?: string;
  watermarkCustomText?: string;
  watermarkOpacity?: number;
  watermarkIsBold?: boolean;
  hideOnTabSwitch?: boolean;
  blurOnTabSwitch?: boolean;
  shieldOnScreenShare?: boolean;
  /** Hide content when browser window loses focus (default: true) */
  hideOnWindowBlur?: boolean;
  /** Enable the GPU video overlay trick (default: true) */
  enableVideoOverlay?: boolean;
  className?: string;
  shieldMessage?: string;
  /** Exempt violations during exam submission phase */
  exemptOnSubmit?: boolean;
}

interface ViolationResult {
  blocked: boolean;
  warned: boolean;
  attempt: number;
  allowedAttempts: number;
  remainingAttempts: number;
  message: string;
}

const SensitiveContent: React.FC<SensitiveContentProps> = ({
  children,
  userId: userIdProp,
  module = 'exam',
  showWatermark = true,
  watermarkColor,
  watermarkCustomText,
  watermarkOpacity,
  watermarkIsBold = true,
  hideOnTabSwitch = true,
  blurOnTabSwitch = false,
  shieldOnScreenShare = true,
  hideOnWindowBlur = false,
  enableVideoOverlay = false,
  className = '',
  shieldMessage = 'Content is hidden while switching tabs. Return to this tab to resume.',
  exemptOnSubmit = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { userId: ctxUserId, sessionId, clearSession } = useSecurityContext();
  const userId = userIdProp || ctxUserId || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userId') || '' : '');

  // === Dynamic Module Protection Status ===
  const [isModuleProtected, setIsModuleProtected] = useState<boolean>(true);
  // === Strict lock — admin controlled: true = block on threshold, false = warn only ===
  const [isStrictLock, setIsStrictLock] = useState<boolean>(true);

  // === Suspension state (permanent block) ===
  const [isPermanentlySuspended, setIsPermanentlySuspended] = useState<boolean>(false);
  const [suspensionReason, setSuspensionReason] = useState<string>('');

  // === Warning state (grace period — threshold not reached yet) ===
  const [warningBanner, setWarningBanner] = useState<{
    visible: boolean;
    attempt: number;
    allowedAttempts: number;
    remainingAttempts: number;
    message: string;
  } | null>(null);

  // Track in-flight calls to avoid duplicate rapid triggers
  const violationInFlightRef = useRef<boolean>(false);

  // Fetch active public security config to check if this module is protected
  useEffect(() => {
    fetch(buildUrl('/public/security/config'))
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data.screenshotProtectedModules)) {
          const protectedList: string[] = data.screenshotProtectedModules;
          setIsModuleProtected(protectedList.includes(module));
        }
        // Read strict lock mode — if disabled, only warn, never block
        if (typeof data.strictScreenshotLock === 'boolean') {
          setIsStrictLock(data.strictScreenshotLock);
        }
      })
      .catch(() => {
        // Fallback: active for exam and results
        setIsModuleProtected(['exam', 'results', 'documents', 'classes'].includes(module));
        setIsStrictLock(true);
      });
  }, [module]);

  const callViolationBlock = useCallback(async (reason: 'screenshot' | 'recording'): Promise<ViolationResult | null> => {
    const activeUserId = userId || sessionStorage.getItem('userId') || '';
    if (!activeUserId) return null;

    const activeTenantId = (typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('activeTenantId') || sessionStorage.getItem('tenantId')) : null) || 'default';

    const payload = JSON.stringify({
      userId: activeUserId,
      reason,
      sessionId: sessionId || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('securitySessionId') || '' : ''),
      module,
      tenantId: activeTenantId,
    });

    // Single awaited fetch — no sendBeacon to avoid double-counting violations in the DB.
    // sendBeacon + fetch = 2 records per violation, which shifts attempt counts and breaks blocking logic.
    try {
      const res = await fetch(buildUrl('/security/violation/block'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': activeTenantId,
          'X-User-Id': activeUserId,
        },
        body: payload,
        // keepalive: true ensures the request completes even if user navigates away
        keepalive: true,
      });
      if (res.ok) {
        return await res.json() as ViolationResult;
      }
    } catch {
      // Backend offline — return a local "block" if strict mode is on to be safe
      if (isStrictLock) {
        return { blocked: true, warned: false, attempt: 1, allowedAttempts: 1, remainingAttempts: 0, message: 'Connection error — applying local security block.' };
      }
    }
    return null;
  }, [userId, sessionId, module, isStrictLock]);

  const triggerViolation = useCallback(async (reason: 'screenshot' | 'recording') => {
    // Exempt if user is submitting the exam
    if (exemptOnSubmit) return;

    // If this module is not in the protected list, bypass
    if (!isModuleProtected) return;

    // Avoid duplicate rapid calls
    if (violationInFlightRef.current || isPermanentlySuspended) return;
    violationInFlightRef.current = true;

    try {
      const result = await callViolationBlock(reason);
      if (result && result.blocked) {
        setIsPermanentlySuspended(true);
        setSuspensionReason(reason);
        sessionStorage.setItem('account_permanently_blocked', 'true');
        setWarningBanner(null);
      } else if (result && result.warned) {
        setWarningBanner({
          visible: true,
          attempt: result.attempt,
          allowedAttempts: result.allowedAttempts,
          remainingAttempts: result.remainingAttempts,
          message: result.message,
        });
        setTimeout(() => setWarningBanner(null), 8000);
      }
    } finally {
      setTimeout(() => { violationInFlightRef.current = false; }, 2000);
    }
  }, [exemptOnSubmit, isModuleProtected, isPermanentlySuspended, callViolationBlock]);

  const {
    isPageHidden,
    isScreenSharing,
    isWindowBlurred,
  } = useScreenProtection({
    blockCopy: isModuleProtected && !exemptOnSubmit,
    blockContextMenu: isModuleProtected && !exemptOnSubmit,
    blockDrag: isModuleProtected && !exemptOnSubmit,
    flashOnPrintScreen: isModuleProtected && !exemptOnSubmit,
    // Enable blur detection only when the module is actively protected
    enableBlurDetection: isModuleProtected && !exemptOnSubmit,
    // Block MediaRecorder-based screen recording
    blockMediaRecorder: isModuleProtected && !exemptOnSubmit,
    onPrintScreenAttempt: () => {
      if (!exemptOnSubmit && isModuleProtected) {
        triggerViolation('screenshot');
      }
    },
    onScreenShareStart: () => {
      if (!exemptOnSubmit && isModuleProtected) {
        triggerViolation('recording');
      }
    },
  });

  // Hardware GPU video overlay
  useVideoOverlayProtection(enableVideoOverlay && isModuleProtected && !exemptOnSubmit);

  const shouldShieldShare = isModuleProtected && shieldOnScreenShare && isScreenSharing;
  const shouldHideTab     = isModuleProtected && hideOnTabSwitch && isPageHidden && !blurOnTabSwitch;
  const shouldBlurTab     = isModuleProtected && blurOnTabSwitch && isPageHidden;
  const shouldHideBlur    = isModuleProtected && hideOnWindowBlur && isWindowBlurred;

  const [userDismissedShield, setUserDismissedShield] = useState<boolean>(false);

  useEffect(() => {
    if (!isPageHidden && !isWindowBlurred && !isScreenSharing) {
      setUserDismissedShield(false);
    }
  }, [isPageHidden, isWindowBlurred, isScreenSharing]);

  // Distinguish permanent suspension from temporary window blur / tab hidden pause
  const isTemporarilyShielded = !isPermanentlySuspended && !userDismissedShield && (shouldShieldShare || shouldHideTab || shouldHideBlur);
  const isBlurred             = !isPermanentlySuspended && shouldBlurTab && !isTemporarilyShielded;
  const isShielded            = isPermanentlySuspended || isTemporarilyShielded;

  const handleExitToLogin = () => {
    clearSession();
    sessionStorage.clear();
    window.location.href = '/login';
  };

  const wrapperClasses = [
    'shine-sensitive-content',
    isBlurred ? 'shine-sensitive-content--blurred' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={containerRef}
      className={wrapperClasses}
      onCopy={(e) => {
        if (!isModuleProtected) return;
        const t = e.target as HTMLElement;
        if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t.isContentEditable) return;
        e.preventDefault();
      }}
      onCut={(e) => {
        if (!isModuleProtected) return;
        const t = e.target as HTMLElement;
        if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t.isContentEditable) return;
        e.preventDefault();
      }}
      onDragStart={(e) => {
        if (!isModuleProtected) return;
        const t = e.target as HTMLElement;
        if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t.isContentEditable) return;
        e.preventDefault();
      }}
      onContextMenu={(e) => {
        if (!isModuleProtected) return;
        const t = e.target as HTMLElement;
        if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t.isContentEditable) return;
        e.preventDefault();
      }}
    >
      {/* ── Protected content (preserved in memory with display hidden when shielded to prevent state destruction and blank screens on resume) ── */}
      <div style={isShielded ? { display: 'none' } : undefined}>
        {children}
      </div>

      {/* ── Anti-capture GPU compositing overlay ── */}
      {!isShielded && isModuleProtected && (
        <div className="shine-anti-capture-layer" aria-hidden="true" />
      )}

      {/* ── Warning Banner (Grace period — threshold NOT yet reached) ── */}
      {!isPermanentlySuspended && warningBanner?.visible && (
        <div className="shine-violation-warning-banner" role="alert">
          <div className="shine-violation-warning-inner">
            <div className="shine-violation-warning-icon" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <div className="shine-violation-warning-text">
              <strong>Security Warning — Attempt {warningBanner.attempt} of {warningBanner.allowedAttempts}</strong>
              <span>
                {warningBanner.remainingAttempts > 0
                  ? `${warningBanner.remainingAttempts} attempt${warningBanner.remainingAttempts !== 1 ? 's' : ''} remaining before your account is permanently blocked.`
                  : 'This was your final warning. Next attempt will permanently block your account.'}
              </span>
            </div>
            <button
              type="button"
              className="shine-violation-warning-close"
              onClick={() => setWarningBanner(null)}
              aria-label="Dismiss warning"
            >
              &#x2715;
            </button>
          </div>
        </div>
      )}

      {/* ── PERMANENT SUSPENSION MODAL (Strict backend block only) ── */}
      {isPermanentlySuspended && (
        <div className="shine-screen-shield shine-screen-suspended-backdrop" role="alert" aria-live="assertive">
          <div className="shine-screen-shield__inner shine-suspended-modal-card">
            {/* Threat Header Strip */}
            <div className="shine-threat-header">
              <span className="shine-threat-header-tag">Security Violation Detected</span>
            </div>

            <div className="shine-modal-body">
              {/* Icon */}
              <div className="shine-screen-shield__icon" aria-hidden="true">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>

              <h2 className="shine-suspended-title">Account Suspended</h2>
              <p className="shine-suspended-subtitle">Your access has been permanently revoked</p>

              <div className="shine-suspended-highlight-msg">
                Your account is suspended due to a security policy violation. Please contact your administrator to request an account unblock.
              </div>

              <div className="shine-lock-note-box">
                <strong>Reason for Suspension</strong>
                {suspensionReason === 'recording'
                  ? 'Screen recording or screen sharing activity was detected during an active exam session. This is a strict violation of the exam integrity policy.'
                  : 'An unauthorized screenshot attempt was detected during an active exam session. This is a strict violation of the exam integrity policy.'}
                {' '}Your exam session has been immediately terminated and this incident has been logged.
              </div>

              <div className="shine-shield-action-box">
                <button
                  type="button"
                  className="shine-shield-unlock-btn"
                  onClick={handleExitToLogin}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  Exit to Login
                </button>
              </div>
            </div>

            <div className="shine-modal-footer-meta">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              Incident ID: {(sessionStorage.getItem('securitySessionId') || 'N/A').slice(0, 12).toUpperCase()}
            </div>
          </div>
        </div>
      )}

      {/* ── TEMPORARY TAB-AWAY / WINDOW-BLUR PAUSE SHIELD (Non-blocking) ── */}
      {!isPermanentlySuspended && isTemporarilyShielded && (
        <div
          className="shine-screen-shield shine-temporary-pause-backdrop"
          role="alert"
        >
          <div className="shine-screen-shield__inner shine-temporary-shield-card">
            {/* Shield icon with pulse */}
            <div className="shine-temporary-shield-icon" aria-hidden="true">
              <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>

            <h3 className="shine-temporary-title">Exam Paused</h3>

            {/* Resume context info extracted from shieldMessage */}
            {shieldMessage ? (
              <div className="shine-resume-info-box">
                <p className="shine-resume-info-text">{shieldMessage}</p>
              </div>
            ) : (
              <p className="shine-temporary-msg">
                Your exam is protected. Return to this tab to resume where you left off.
              </p>
            )}

            {/* Live pulse indicator */}
            <div className="shine-resume-pulse-row" aria-hidden="true">
              <span className="shine-pulse-dot" />
              <span style={{ fontSize: '0.78rem', color: '#7dd3fc', fontWeight: 600 }}>
                Timer paused — your answers are saved
              </span>
            </div>

            {/* Resume CTA */}
            <button
              type="button"
              className="shine-shield-unlock-btn shine-resume-btn"
              onClick={(e) => {
                e.stopPropagation();
                setUserDismissedShield(true);
                try {
                  window.focus();
                } catch {}
              }}
            >
              ▶ Resume Exam
            </button>

            <p className="shine-resume-hint">
              Tap anywhere or press Resume to continue
            </p>
          </div>
        </div>
      )}

      {/* ── Dynamic watermark (rendered if enabled; DynamicWatermark checks admin config) ── */}
      {showWatermark && (
        <DynamicWatermark
          module={module as any}
          userId={userId}
          color={watermarkColor}
          customText={watermarkCustomText}
          opacity={watermarkOpacity}
          isBold={watermarkIsBold}
        />
      )}
    </div>
  );
};

export default SensitiveContent;
